import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useCredits, CreditTransaction } from '@/hooks/useCredits';
import { useCreditPackages, CreditPackage } from '@/hooks/useCreditPackages';
import { useCheckout } from '@/hooks/useCheckout';
import {
    CircleStackIcon,
    CheckCircleIcon,
    InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';

function PackageCard({
    pkg,
    baselinePricePerCredit,
    isSelected,
    onSelect,
}: {
    pkg: CreditPackage;
    baselinePricePerCredit: number;
    isSelected: boolean;
    onSelect: () => void;
}) {
    const pricePerCredit = pkg.priceInCents / pkg.credits;
    const discount = baselinePricePerCredit > 0
        ? Math.round((1 - pricePerCredit / baselinePricePerCredit) * 100)
        : 0;

    return (
        <button
            type="button"
            onClick={onSelect}
            className={`relative rounded-lg border-2 p-3 text-left transition-all ${
                isSelected
                    ? 'border-primary-600 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20'
                    : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-secondary dark:hover:border-gray-500'
            }`}
        >
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">
                        {pkg.credits} credits
                    </p>
                    <p className="text-base text-gray-600 dark:text-gray-300">
                        {pkg.priceFormatted}
                    </p>
                </div>
                <div
                    className={`h-5 w-5 rounded-full border-2 ${
                        isSelected
                            ? 'border-primary-600 bg-primary-600 dark:border-primary-500 dark:bg-primary-500'
                            : 'border-gray-300 dark:border-gray-600'
                    }`}
                >
                    {isSelected && (
                        <CheckCircleIcon className="h-4 w-4 text-white" />
                    )}
                </div>
            </div>
            {discount > 0 && (
                <span className="absolute -top-2 right-2 rounded-sm bg-green-500 px-2 py-0.5 text-xs font-medium text-white">
                    {discount}% off
                </span>
            )}
        </button>
    );
}

function RefundPercentageBadge({ percentage }: { percentage: number }) {
    const isFullRefund = percentage === 100;
    const badgeClass = isFullRefund
        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';

    return (
        <span
            className={`ml-1 rounded-sm px-1.5 py-0.5 text-xs font-medium ${badgeClass}`}
            data-testid="refund-percentage-badge"
        >
            ({percentage}%)
        </span>
    );
}

function TransactionRow({ transaction }: { transaction: CreditTransaction }) {
    const isCredit = ['purchase', 'manual_add', 'refund'].includes(transaction.type);
    const isRefund = transaction.type === 'refund';
    const date = new Date(transaction.createdAt);

    return (
        <div className="flex items-center justify-between border-b border-gray-200 py-3 last:border-0 dark:border-gray-700">
            <div className="flex-1">
                <p className="font-medium text-gray-900 dark:text-white">
                    {transaction.description}
                    {isRefund && transaction.refundPercentage && (
                        <RefundPercentageBadge percentage={transaction.refundPercentage} />
                    )}
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="rounded-sm bg-gray-100 px-2 py-0.5 text-xs dark:bg-gray-700">
                        {transaction.typeLabel}
                    </span>
                    <span>{date.toLocaleDateString()}</span>
                </div>
            </div>
            <span
                className={`text-lg font-semibold ${
                    isCredit ? 'text-green-600' : 'text-red-600'
                }`}
            >
                {isCredit ? '+' : '-'}{transaction.amount}
            </span>
        </div>
    );
}

export function CreditsPage() {
    usePageTitle('Credits');

    const [searchParams] = useSearchParams();
    const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(null);

    const { config } = useAuth();
    const { data: creditsData, isLoading: isLoadingCredits, refetch: refetchCredits } = useCredits();
    const { data: packagesData, isLoading: isLoadingPackages } = useCreditPackages();
    const checkout = useCheckout();

    // Handle success/cancel URL params from Stripe redirect
    useEffect(() => {
        const success = searchParams.get('success');
        const canceled = searchParams.get('canceled');

        if (success === 'true') {
            toast.success('Payment successful! Your credits have been added.');
            refetchCredits();
            // Clean up URL
            window.history.replaceState({}, '', '/credits');
        } else if (canceled === 'true') {
            toast.info('Payment was canceled.');
            window.history.replaceState({}, '', '/credits');
        }
    }, [searchParams, refetchCredits]);

    const handlePurchase = async () => {
        if (!selectedPackage) return;

        try {
            const result = await checkout.mutateAsync({ credits: selectedPackage.credits });
            // Redirect to Stripe checkout
            window.location.href = result.url;
        } catch {
            toast.error('Failed to create checkout session. Please try again.');
        }
    };

    if (isLoadingCredits || isLoadingPackages) {
        return (
            <Layout>
                <div className="flex min-h-[400px] items-center justify-center">
                    <Spinner size="lg" />
                </div>
            </Layout>
        );
    }

    // Filter to only show packages with 20+ credits
    const displayPackages = packagesData?.packages.filter(pkg => pkg.credits >= 20) ?? [];

    // Use smallest package's price-per-credit as baseline for discount calculation
    const smallestPackage = displayPackages[0];
    const baselinePricePerCredit = smallestPackage
        ? smallestPackage.priceInCents / smallestPackage.credits
        : 0;

    return (
        <Layout>
            <div className="space-y-6">
                <PageHeader title="Credits" />

                {/* Current Balance */}
                <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-secondary">
                    <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900">
                            <CircleStackIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Current Balance
                            </p>
                            <p className="text-3xl font-bold text-gray-900 dark:text-white">
                                {creditsData?.balance ?? 0} credits
                            </p>
                        </div>
                    </div>
                </div>

                {/* How Credits Work + Refund Policy */}
                <div
                    className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20"
                    data-testid="refund-policy-info"
                >
                    <InformationCircleIcon className="h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" />
                    <div className="text-sm text-blue-800 dark:text-blue-200">
                        <p>
                            <span className="font-medium">Credit costs:</span>{' '}
                            {config?.credits.costs.ledger ?? 3} per asset/debt, {config?.credits.costs.ledgerReturn ?? 2} per cash flow, {config?.credits.costs.ledgerValue ?? 1} per value entry.
                            Credits never expire.
                        </p>
                        <p className="mt-1">
                            <span className="font-medium">Refunds:</span>{' '}
                            Full purchase refund within 30 days, no questions asked. Deleting entries refunds 100% of credits within 30 days, 50% thereafter.
                        </p>
                    </div>
                </div>

                {/* Package Selection */}
                <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-secondary">
                    <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                        Purchase Credits
                    </h2>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        {displayPackages.map((pkg) => (
                            <PackageCard
                                key={pkg.credits}
                                pkg={pkg}
                                baselinePricePerCredit={baselinePricePerCredit}
                                isSelected={selectedPackage?.credits === pkg.credits}
                                onSelect={() => setSelectedPackage(pkg)}
                            />
                        ))}
                    </div>
                    <div className="mt-4 flex justify-center">
                        <Button
                            onClick={handlePurchase}
                            disabled={!selectedPackage || checkout.isPending}
                            isLoading={checkout.isPending}
                        >
                            {selectedPackage
                                ? `Buy ${selectedPackage.credits} Credits for ${selectedPackage.priceFormatted}`
                                : 'Select a Package'}
                        </Button>
                    </div>
                </div>

                {/* Recent Transactions - only show if there are transactions */}
                {creditsData?.recentTransactions && creditsData.recentTransactions.length > 0 && (
                    <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-secondary">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Recent Transactions
                            </h2>
                            <Link
                                to="/credits/history"
                                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                            >
                                View All
                            </Link>
                        </div>
                        <div className="divide-y divide-gray-200 dark:divide-accent">
                            {creditsData.recentTransactions.map((transaction) => (
                                <TransactionRow
                                    key={transaction.id}
                                    transaction={transaction}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}
